import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, useToast } from '@caruso/ui';
import {
  departmentsService,
  DepartmentUser,
} from '@/services/departments.service';
import { usersService } from '@/services/users.service';
import {
  ArrowLeft,
  Plus,
  Search,
  Trash2,
  Shield,
  ShieldOff,
  User,
  Users,
  Building2,
  Loader2,
  X,
} from 'lucide-react';
import { cn } from '@caruso/ui';

export function DepartmentUsersPage() {
  const { id: departmentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Buscar departamento
  const { data: department, isLoading: isLoadingDepartment } = useQuery({
    queryKey: ['department', departmentId],
    queryFn: () => departmentsService.getById(departmentId!),
    enabled: !!departmentId,
  });

  // Buscar usuarios do departamento
  const { data: departmentUsers, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['department-users', departmentId],
    queryFn: () => departmentsService.getUsers(departmentId!),
    enabled: !!departmentId,
  });

  // Buscar todos os usuarios (para adicionar)
  const { data: allUsers } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.getAll(),
    enabled: isAddingUser,
  });

  // Adicionar usuario
  const addUserMutation = useMutation({
    mutationFn: ({ userId, isManager }: { userId: string; isManager: boolean }) =>
      departmentsService.addUser(departmentId!, { userId, isManager }),
    onSuccess: () => {
      toast({
        title: 'Usuario adicionado',
        description: 'Usuario adicionado ao departamento com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['department-users', departmentId] });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setIsAddingUser(false);
      setSelectedUserId('');
    },
    onError: () => {
      toast({
        title: 'Erro ao adicionar',
        description: 'Nao foi possivel adicionar o usuario',
        variant: 'destructive',
      });
    },
  });

  // Remover usuario
  const removeUserMutation = useMutation({
    mutationFn: (userId: string) => departmentsService.removeUser(departmentId!, userId),
    onSuccess: () => {
      toast({
        title: 'Usuario removido',
        description: 'Usuario removido do departamento',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['department-users', departmentId] });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: () => {
      toast({
        title: 'Erro ao remover',
        description: 'Nao foi possivel remover o usuario',
        variant: 'destructive',
      });
    },
  });

  // Alterar role (gerente)
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, isManager }: { userId: string; isManager: boolean }) =>
      departmentsService.updateUserRole(departmentId!, userId, isManager),
    onSuccess: () => {
      toast({
        title: 'Permissao atualizada',
        description: 'Permissao do usuario atualizada com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['department-users', departmentId] });
    },
    onError: () => {
      toast({
        title: 'Erro ao atualizar',
        description: 'Nao foi possivel atualizar a permissao',
        variant: 'destructive',
      });
    },
  });

  const handleRemoveUser = (userId: string, userName: string) => {
    if (!confirm(`Tem certeza que deseja remover "${userName}" do departamento?`)) return;
    removeUserMutation.mutate(userId);
  };

  const handleToggleManager = (userId: string, currentIsManager: boolean) => {
    updateRoleMutation.mutate({ userId, isManager: !currentIsManager });
  };

  const handleAddUser = () => {
    if (!selectedUserId) return;
    addUserMutation.mutate({ userId: selectedUserId, isManager: false });
  };

  // Filtrar usuarios que ja nao estao no departamento
  const availableUsers = allUsers?.filter(
    (user: any) => !departmentUsers?.some((du) => du.user.id === user.id)
  );

  // Filtrar usuarios por busca
  const filteredUsers = departmentUsers?.filter((du) => {
    const searchLower = search.toLowerCase();
    return (
      du.user.name.toLowerCase().includes(searchLower) ||
      du.user.email.toLowerCase().includes(searchLower)
    );
  });

  if (isLoadingDepartment) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brand-gold animate-spin" />
      </div>
    );
  }

  if (!department) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-white mb-4">Departamento nao encontrado</h2>
        <Button onClick={() => navigate('/app/departamentos')}>Voltar</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/app/departamentos')}
            className="text-brand-gold hover:bg-brand-gold/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-brand-gold" />
              <h1 className="text-3xl font-bold text-white">{department.name}</h1>
            </div>
            <p className="text-brand-ivory/80 mt-1">
              Gerenciar usuarios do departamento
            </p>
          </div>
        </div>
        <Button
          onClick={() => setIsAddingUser(true)}
          className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90 font-semibold"
        >
          <Plus className="w-5 h-5 mr-2" />
          Adicionar Usuario
        </Button>
      </div>

      {/* Add User Modal */}
      {isAddingUser && (
        <Card className="p-6 bg-brand-smoke border-brand-gold/40">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Adicionar Usuario</h3>
            <button
              onClick={() => {
                setIsAddingUser(false);
                setSelectedUserId('');
              }}
              className="text-brand-ivory/50 hover:text-brand-gold"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                Selecione um usuario
              </label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-lg text-brand-ivory focus:outline-none focus:border-brand-gold/30"
              >
                <option value="">Selecione...</option>
                {availableUsers?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
              {availableUsers?.length === 0 && (
                <p className="text-sm text-brand-ivory/50 mt-2">
                  Todos os usuarios ja estao neste departamento
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleAddUser}
                disabled={addUserMutation.isPending || !selectedUserId}
                className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90"
              >
                {addUserMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Adicionar
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddingUser(false);
                  setSelectedUserId('');
                }}
                className="border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Search */}
      <Card className="p-6 bg-brand-smoke border-brand-gold/20">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold/50" />
          <input
            type="text"
            placeholder="Buscar usuario por nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-lg text-brand-ivory placeholder:text-brand-ivory/40 focus:outline-none focus:border-brand-gold/30"
          />
        </div>
      </Card>

      {/* Users List */}
      <div className="space-y-4">
        {isLoadingUsers ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 text-brand-gold animate-spin" />
          </div>
        ) : filteredUsers && filteredUsers.length > 0 ? (
          filteredUsers.map((du) => (
            <motion.div
              key={du.user.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="p-4 bg-brand-smoke border-brand-gold/20 hover:border-brand-gold/40 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      'w-12 h-12 rounded-full flex items-center justify-center',
                      du.isManager
                        ? 'bg-gradient-to-br from-brand-gold to-yellow-600'
                        : 'bg-brand-smoke/50'
                    )}>
                      <User className={cn(
                        'w-6 h-6',
                        du.isManager ? 'text-white' : 'text-brand-ivory/70'
                      )} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white">{du.user.name}</h3>
                        {du.isManager && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-gold/20 text-brand-gold border border-brand-gold/30">
                            <Shield className="w-3 h-3" />
                            Gerente
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-brand-ivory/60">{du.user.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleManager(du.user.id, du.isManager)}
                      disabled={updateRoleMutation.isPending}
                      className={cn(
                        'border-brand-gold/30 hover:bg-brand-gold/10',
                        du.isManager ? 'text-orange-400' : 'text-brand-gold'
                      )}
                    >
                      {du.isManager ? (
                        <>
                          <ShieldOff className="w-4 h-4 mr-2" />
                          Remover Gerente
                        </>
                      ) : (
                        <>
                          <Shield className="w-4 h-4 mr-2" />
                          Tornar Gerente
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveUser(du.user.id, du.user.name)}
                      disabled={removeUserMutation.isPending}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))
        ) : (
          <Card className="p-12 bg-brand-smoke border-brand-gold/20">
            <div className="text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-brand-gold/30" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {search ? 'Nenhum usuario encontrado' : 'Nenhum usuario neste departamento'}
              </h3>
              <p className="text-brand-ivory/60 mb-6">
                {search
                  ? 'Tente ajustar os termos da busca'
                  : 'Adicione usuarios para que eles possam atender conversas deste departamento'}
              </p>
              {!search && (
                <Button
                  onClick={() => setIsAddingUser(true)}
                  className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Adicionar Usuario
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
